import React, { FC, useEffect } from 'react';
import * as Sentry from '@sentry/browser';
import { cssRule } from 'typestyle';
import { refreshUserInfo, trackAction } from './libs/analytics/ActionTracking';
import { startVersionCheckLoop } from './modules/versionChecker';
import { setLang } from './libs/i18n/i18n';
import WindowManager from './components/WindowManager';
import { fetchConfig } from './modules/config/ConfigService';
import query_string_utils from './libs/queryStringUtils';
import { useAppStore } from './store/appStore';
import MainContainer from './components/main_container/MainContainer';
import { enableMapSet } from 'immer';
import TokenService from './api_services/TokenService/TokenService';
import { Layout } from './components/layout/Layout';
import { Sidebar } from './components/layout/sidebar/Sidebar';
import { Content } from './components/layout/content/Content';
import { useDocumentsStore } from './store/documentsStore';
import getPilotApiService from './api_services/PilotApiService';
import DocAlertPopup from './components/main_container/documents/DocAlertPopup';
import Logger from './libs/logger/Logger';
import { typeCompact14RegularProperties, MessagesHost } from '@traceair/tds';
import { useCurrentUserType } from './hooks/useCurrentUserType';
import { useRestoreUploads } from './store/restoreUploads/useRestoreUploads';
import { useMeasureUploadSpeed } from './modules/intakeLoop/upload/measurements/useMeasureUploadSpeed';
import { intercomSingleton } from './libs/intercom/init';
import { PERMISSIONS_TO_VIEW_MISSIONS } from './consts/permissions';
import { forceHashScroll } from './libs/forceHashScroll';
import { redirectFromExperimentalToProdIfNecessary } from './redirectFromExperimentalToProdIfNecessary';
import { Alerts } from './components/misc/Alerts';

enableMapSet();

// TDS6 dropdowns get lost behind our components
cssRule('.ant-select-dropdown', {
  zIndex: 99999999,
});

cssRule('.react-datepicker', {
  ...typeCompact14RegularProperties,
});

const useLoadApp = () => {
  const { setUser, setPermissions } = useAppStore();

  useEffect(() => {
    const initializeApp = async () => {
      const token = await TokenService.updateToken(null, PERMISSIONS_TO_VIEW_MISSIONS);
      const user = token.payload.user;
      const permissions = token.payload.ACL;

      refreshUserInfo();
      await fetchConfig();
      trackAction('App is opened', null, { isInitial: true });

      if (user) {
        setLang(user.lang || 'en');
        redirectFromExperimentalToProdIfNecessary();

        if (LogConfig.sentry.enabled) {
          Sentry.getCurrentScope().setTag('isExperimental', query_string_utils.checkIfExperimental()).setUser({
            email: user.email,
          });
        }
      }

      setUser(user);
      setPermissions(permissions);
    };

    initializeApp();
    startVersionCheckLoop();
  }, [setUser]);
};

const useLoadPilotData = () => {
  const { setMissions, showWindow, setPilot } = useAppStore();

  const { setDocuments, setIsInitialFetchingDone: setIsInitialDocFetchingDone } = useDocumentsStore();

  useEffect(() => {
    const initializeData = async () => {
      const pilotApiService = getPilotApiService();

      try {
        // Even if there is a 403 error, users can still access missions
        const pilot = await pilotApiService.getPilotInfo().catch((e) => {
          if (e.response.status === 403) {
            return null;
          }

          throw e;
        });

        setPilot(pilot);

        const hasSchedulesAndDocs = Boolean(pilot?.id);
        const fetchedMissions = await pilotApiService.getMissions(hasSchedulesAndDocs);

        setMissions(fetchedMissions);

        setTimeout(forceHashScroll);

        if (hasSchedulesAndDocs) {
          const docsResponse = await pilotApiService.getPilotDocuments();

          setDocuments(docsResponse);
          setIsInitialDocFetchingDone();
          showWindow(<DocAlertPopup />);
        }
      } catch (error) {
        setPilot(null);
        setMissions([]);
        Logger.error(error as Error | string);
      }
    };

    initializeData();
  }, []);
};

const useLogUserType = () => {
  const userType = useCurrentUserType();

  useEffect(() => {
    if (userType) {
      Logger.log(`Current user type: ${userType}`);

      const { user } = useAppStore.getState();
      if (user) {
        intercomSingleton.initIntercom(user, userType);
      }
    }
  }, [userType]);
};

const App: FC = () => {
  useLoadApp();
  useLoadPilotData();
  useLogUserType();
  useRestoreUploads();
  useMeasureUploadSpeed();

  return (
    <Layout>
      <Sidebar />
      <Content>
        <MainContainer />
        <WindowManager />
        <Alerts />
        <MessagesHost placement="bottom" />
      </Content>
    </Layout>
  );
};

export default App;
